import { Activity, AlertCircle, Globe, Zap } from "lucide-react";
import type { FpMessagePart } from "../../../../../schemas/codegen";
import type { RuntimeErrorEvent } from "../../../../../schemas/runtime-errors";
import {
  ExpandableMessageCard,
  SimpleMessageCard,
  isBootsmanResult,
} from "./shared";

interface RuntimeLogsProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}

type RuntimeLogsResult = {
  logs: RuntimeErrorEvent[];
  totalCount: number;
  appName: string;
};

/**
 * Component for displaying runtime logs from the deployed application.
 */
export function RuntimeLogs({ part }: RuntimeLogsProps) {
  const isError = part.status === "error";

  // Extract runtime logs result if available
  const runtimeLogsResult = (() => {
    if (
      part.status === "complete" &&
      part.result &&
      isBootsmanResult(part.result) &&
      part.result.success
    ) {
      return part.result.data as RuntimeLogsResult;
    }
    return null;
  })();

  const getStatusText = () => {
    if (part.status === "pending") {
      return "Fetching runtime logs...";
    }

    if (isError) {
      return "Failed to fetch runtime logs";
    }

    if (runtimeLogsResult) {
      const { totalCount } = runtimeLogsResult;
      if (totalCount === 0) {
        return "No runtime logs found";
      }
      return `Fetched ${totalCount} log${totalCount !== 1 ? "s" : ""}`;
    }

    return "Runtime logs fetched";
  };

  // If no logs or pending, show simple status
  if (!runtimeLogsResult?.logs.length || part.status !== "complete") {
    return (
      <SimpleMessageCard
        status={part.status}
        icon={Activity}
        title={getStatusText()}
      />
    );
  }

  // Show accordion for logs
  return (
    <ExpandableMessageCard
      status={part.status}
      result={part.result}
      icon={Activity}
      title={getStatusText()}
      accordionValue="runtime-logs"
    >
      <div className="relative">
        <div className="max-h-96 overflow-y-auto space-y-3">
          {runtimeLogsResult.logs.map((log, index) => (
            <div
              key={`${log.ts}-${index}`}
              className="p-3 bg-background rounded-md border space-y-2"
            >
              {/* Timestamp */}
              <div className="text-xs text-muted-foreground font-mono">
                {new Date(log.ts).toLocaleString()}
              </div>

              {/* HTTP Request - Top level */}
              {log.fetchCall && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <Globe className="w-3.5 h-3.5" />
                    HTTP Request
                  </div>
                  <div className="text-sm font-mono bg-muted/50 p-2 rounded">
                    <span className="font-semibold">
                      {log.fetchCall.method}
                    </span>{" "}
                    {log.fetchCall.url}
                  </div>
                </div>
              )}

              {/* Exceptions - Second level */}
              {log.exceptions.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-destructive">
                    <AlertCircle className="w-3.5 h-3.5" />
                    Exceptions
                  </div>
                  <div className="space-y-1">
                    {log.exceptions.map((exception, exIndex) => {
                      const exceptionStr =
                        typeof exception === "string"
                          ? exception
                          : JSON.stringify(exception, null, 2);
                      return (
                        <div
                          key={`${log.ts}-exception-${exIndex}-${exceptionStr.slice(0, 20)}`}
                          className="text-sm font-mono bg-destructive/10 p-2 rounded text-wrap break-all"
                        >
                          {exceptionStr}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Console logs - Third level */}
              {log.logs.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    <Zap className="w-3.5 h-3.5" />
                    Console Logs
                  </div>
                  <div className="space-y-1">
                    {log.logs.map((logLine, logIndex) => (
                      <div
                        key={`${log.ts}-log-${logIndex}-${logLine.slice(0, 20)}`}
                        className="text-sm font-mono bg-muted/50 p-2 rounded text-wrap break-all"
                      >
                        {logLine}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
        {/* Bottom fade gradient to indicate scrollability */}
        <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-input/40 to-transparent pointer-events-none" />
      </div>
    </ExpandableMessageCard>
  );
}
